import api from './api';
import { User } from './authService';

export interface SkoolCommunityInfo {
  showSkoolCommunity: boolean;
  skoolCommunityUrl: string | null;
  openInNewTabHint?: string;
}

export interface SubscriptionSummary {
  hasSubscription: boolean;
  isActive?: boolean;
  exemptFromLimits?: boolean;
  message?: string;
  tierKey?: string;
  displayName?: string;
  status?: string;
  accountType?: string;
  currentPeriodStart?: string;
  currentPeriodEnd?: string;
  accessExpiresAt?: string;
  cancelledAt?: string | null;
  cancelAtPeriodEnd?: boolean;
  canCancel?: boolean;
  renewUrl?: string;
  changePackageUrl?: string;
  access?: {
    phase: 'active' | 'grace' | 'soft_locked' | 'none';
    canUsePaidFeatures: boolean;
    showPaymentBanner: boolean;
    billingEndsAt?: string | null;
    graceEndsAt?: string | null;
    daysUntilBillingEnd?: number | null;
    daysUntilSoftLock?: number | null;
  };
  usage?: {
    reportsGenerated: number;
    reportsDownloaded: number;
    savedReportsUsed: number;
  };
  limits?: {
    reportsPerPeriod: number;
    savedReportsLimit: number;
    historyMonths: number;
    reportsRemaining: number | null;
    reportsUsed: number;
    savedReportsRemaining: number | null;
    historyAccessLabel: string;
    reportsPerPeriodLabel: string | number;
    savedReportsLimitLabel: string | number;
  };
}

export interface PackageCatalogItem {
  tierKey: string;
  displayName: string;
  priceLabel?: string;
  billingPeriod: string;
  reportsPerPeriod: number | null;
  savedReportsLimit: number | null;
  historyMonths: number | null;
  accessDurationDays: number | null;
}

export interface PackageCatalog {
  items: PackageCatalogItem[];
  renewUrl: string;
  changePackageUrl: string;
}

export interface UpdateProfileData {
  firstName?: string;
  lastName?: string;
  phone?: string | null;
}

export const accountService = {
  async getSkoolCommunityInfo(): Promise<SkoolCommunityInfo> {
    const response = await api.get<SkoolCommunityInfo>('/account/skool-community');
    return response.data;
  },

  async getSubscriptionSummary(): Promise<SubscriptionSummary> {
    const response = await api.get<SubscriptionSummary>('/account/subscription');
    return response.data;
  },

  async getPackages(): Promise<PackageCatalog> {
    const response = await api.get<PackageCatalog>('/account/packages');
    return response.data;
  },

  async cancelSubscription(): Promise<{
    message: string;
    accessEndsAt?: string | null;
    subscription: SubscriptionSummary;
  }> {
    const response = await api.post<{
      message: string;
      accessEndsAt?: string | null;
      subscription: SubscriptionSummary;
    }>('/account/subscription/cancel');
    return response.data;
  },

  async updateProfile(data: UpdateProfileData): Promise<{ message: string; user: User }> {
    const response = await api.patch<{ message: string; user: Record<string, unknown> }>(
      '/account/profile',
      data
    );
    const raw = response.data.user;
    return {
      message: response.data.message,
      user: {
        id: String(raw.id || raw._id || ''),
        email: String(raw.email || ''),
        firstName: String(raw.firstName || ''),
        lastName: String(raw.lastName || ''),
        phone: raw.phone != null && raw.phone !== '' ? String(raw.phone) : null,
        role: String(raw.role || 'USER'),
        status: raw.status ? String(raw.status) : undefined,
        needsPasswordSetup: Boolean(raw.needsPasswordSetup),
        createdAt: raw.createdAt ? String(raw.createdAt) : undefined,
      },
    };
  },
};
